/**
 * DaVinci Availability Check Service
 *
 * High-level wrapper for checking offer availability using DaVinci API BA action.
 * This service integrates with the DaVinci API to verify booking availability.
 */

import { performBookingAction } from './performAction';
import {
  formatDateForDaVinci,
  createDefaultClient,
  createMultiRoomServices,
  createParticipantsFromInfo,
} from './helpers';
import { DAVINCI_CONFIG } from './config';
import { DaVinciApiError } from '@/types/errors';
import type { RoomCombination } from '@/services/mappers/offerMapper';
import {
  isAvailableCode as isAvailableCodeFromRegistry,
  isFixedBooking as isFixedBookingFromRegistry,
  isRequestBooking,
  isConfirmationBooking,
  getCodeMeta,
} from '@/constants/davinci/returnCodes';

// Re-export helper functions from centralized registry
export const isAvailableCode = isAvailableCodeFromRegistry;
export const isFixedBooking = isFixedBookingFromRegistry;
export const isRequestBookingRequired = isRequestBooking;
export const isConfirmationRequired = isConfirmationBooking;

/**
 * Get user-friendly message for return code
 * Uses centralized registry metadata for consistent messaging.
 *
 * @param code - DaVinci return code
 * @returns Descriptive message
 */
function getMessageForCode(code: number): string {
  const meta = getCodeMeta(code);
  if (meta) {
    return meta.fallbackMessage;
  }
  // Success codes (from B/BQ/BC actions)
  if (code === 200) {
    return 'Booking confirmed';
  }
  if (code === 206) {
    return 'Request booking successful';
  }
  return 'Not available';
}

/**
 * Phase 3 Multi-Room: Parameters for multi-room availability check
 *
 * Used to check availability of a complete room combination (multiple rooms)
 * with a unified passenger list across all rooms.
 */
export interface MultiRoomAvailabilityCheckParams {
  /**
   * The complete room combination to check (may have multiple products/rooms)
   */
  combination: RoomCombination;

  /**
   * Product Code (GIATA ID)
   * Mapped to DaVinci API's "Service" field
   * @example "HERIRINI119"
   */
  productCode: string;

  /**
   * TravelType to send. Checkout's first BA passes '' (blank) so DaVinci returns
   * the correct value. Omit to fall back to DAVINCI_CONFIG.TRAVEL_TYPE.
   */
  travelType?: string;

  /**
   * AgencyNumber to send. Omit to fall back to DAVINCI_CONFIG.AGENCY_NUMBER (1).
   */
  agencyNumber?: number;
}

/**
 * Phase 3 Multi-Room: Result of multi-room availability check
 *
 * Identifies the room combination that was checked rather than a specific offer.
 */
export interface MultiRoomAvailabilityCheckResult {
  code: number;
  message: string;
  available: boolean;

  /** Unique combination identifier (concatenated offer IDs) */
  combinationId: string;

  /**
   * Which BA lifecycle stage produced this result.
   * 'initial' = BA #1 on page mount (dummy client, hotel only)
   * 'preSubmit' = BA #2 on step 1→2 (real client, hotel + auxiliaries)
   */
  baStage?: 'initial' | 'preSubmit';

  // For future BQ/B/BC flow (stored but not used yet)
  sessionId?: string;
  activityId?: string;
  bookingResponse?: Record<string, unknown>;
}

/**
 * Phase 3 Multi-Room: Check availability for complete room combination
 *
 * Checks availability of multiple rooms booked together in a single request.
 * DaVinci API receives multiple services in one BA action, with each room
 * assigned its specific passengers.
 *
 * This is the Phase 3 enhancement for multi-room support. The key improvement
 * is sending multiple services in a single DaVinci request, with proper
 * passenger assignment per room.
 *
 * Return codes:
 * - 712: Fixed booking available (use action B)
 * - 733: Request booking available (use action BQ)
 * - 923: Booking needs confirmation (use action BC)
 * - Other: Error/Unavailable
 *
 * @param params - Multi-room combination and product code
 * @returns Availability result for the complete combination
 *
 * @example
 * const result = await checkMultiRoomAvailability({
 *   combination: {
 *     products: [
 *       { roomCode: 'D03', boardCode: 'H', passengerIds: [1, 2], ... },
 *       { roomCode: 'D01', boardCode: 'H', passengerIds: [3], ... }
 *     ],
 *     allPassengers: [
 *       { id: 1, type: 'adult' },
 *       { id: 2, type: 'adult' },
 *       { id: 3, type: 'child', age: 8 }
 *     ],
 *     startDate: '2025-11-15',
 *     endDate: '2025-11-22',
 *     ...
 *   },
 *   productCode: 'HERIRINI119'
 * });
 * // Returns: { code: 712, available: true, combinationId: '15-1', ... }
 */
export async function checkMultiRoomAvailability(
  params: MultiRoomAvailabilityCheckParams
): Promise<MultiRoomAvailabilityCheckResult> {
  try {
    const services = createMultiRoomServices(params.combination, params.productCode);
    const participants = createParticipantsFromInfo(params.combination.allPassengers);

    // Build multi-service request
    const bookingRequest = {
      ActionCode: 'BA' as const,
      AgencyNumber: params.agencyNumber ?? DAVINCI_CONFIG.AGENCY_NUMBER,
      Touroperator: DAVINCI_CONFIG.TOUROPERATOR,
      TravelType: params.travelType ?? DAVINCI_CONFIG.TRAVEL_TYPE,
      SalesOffice: '',
      BookingDate: formatDateForDaVinci(new Date()),
      Services: services, // Multiple services for multi-room
      Client: createDefaultClient(),
      Participants: participants,
    };

    const login = {};

    const response = await performBookingAction(bookingRequest, login);

    const booking = response.Booking as Record<string, unknown>;
    const returnCode = booking.ReturnCode as number;
    const returnCodeDescription = booking.ReturnCodeDescription as string | undefined;
    const available = isAvailableCode(returnCode);

    // Generate combination ID from product IDs
    const combinationId = params.combination.products.map((p) => p.offerId).join('-');

    return {
      code: returnCode,
      message: returnCodeDescription || getMessageForCode(returnCode),
      available,
      combinationId,
      baStage: 'initial',
      // Store for future BQ/B/BC flow
      sessionId: response.SessionId,
      activityId: response.ActivityId,
      bookingResponse: booking,
    };
  } catch (error) {
    if (error instanceof DaVinciApiError) {
      const combinationId = params.combination.products.map((p) => p.offerId).join('-');
      return {
        code: 999,
        message: error.message,
        available: false,
        combinationId,
      };
    }
    throw error;
  }
}
